/**
 * Version check & force-refresh on stale deploy.
 *
 * On boot: fetches /version.txt, compares with the baked __BUILD_ID__,
 * and hard-reloads if they differ.
 *
 * On visibilitychange -> visible: re-fetches and invokes onStale(remote)
 * exactly once if the remote version differs, so the caller can pause
 * the running game and prompt the user via a modal.
 *
 * All fetch errors are silent no-ops — a flaky network should never
 * block boot or interrupt gameplay.
 */

declare const __BUILD_ID__: string;

export interface VersionCheckOptions {
  /**
   * Invoked when a stale version is detected while the tab is visible
   * (e.g. tab regained focus after a new deploy). Fired at most once
   * per session — subsequent focus events do not re-fire.
   * The callback is responsible for pausing the game and showing a
   * modal prompting the user to reload.
   */
  onStale: (remoteVersion: string) => void;
}

/**
 * Fetch the remote /version.txt. Returns the trimmed body string on 2xx
 * with a non-empty body, or null on any error / non-2xx / empty body.
 *
 * Appends a cache-busting query param and uses `cache: 'no-store'` so
 * intermediaries can't serve a stale value.
 */
export async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.txt?t=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return null;
    const text = await res.text();
    const trimmed = text.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

/**
 * Hard-reload via `location.replace` with a `?v=<buildId>` query string.
 * The query string defeats any cached HTML at the browser/CDN layer;
 * `replace` avoids polluting history.
 */
function hardReload(remoteVersion: string): void {
  const target = window.location.pathname + '?v=' + encodeURIComponent(remoteVersion);
  window.location.replace(target);
}

/**
 * Initialize version checking. Safe to call once at app boot.
 *
 * - Immediately (async) fetches the remote version and hard-reloads if stale.
 * - Registers a `visibilitychange` listener that fires `onStale` exactly once
 *   per session the first time a stale version is observed on tab focus.
 *
 * Includes an infinite-reload guard: if the current URL already has a
 * `?v=` param matching the local `__BUILD_ID__`, we assume a stale CDN
 * and log a warning instead of reloading again.
 */
export function initVersionCheck(options: VersionCheckOptions): void {
  const local = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : '';
  let staleHandled = false;

  // Infinite-reload guard: if URL already has ?v=<local>, and version.txt
  // still claims something different, don't reload again this session.
  let reloadGuardActive = false;
  try {
    const params = new URLSearchParams(window.location.search);
    const vParam = params.get('v');
    if (vParam && vParam === local) {
      reloadGuardActive = true;
    }
  } catch {
    // Ignore malformed URL — best-effort guard only.
  }

  // Boot check
  void fetchRemoteVersion().then((remote) => {
    if (remote === null) return; // silent on fetch error
    if (remote === local) return;

    if (reloadGuardActive) {
      // eslint-disable-next-line no-console
      console.warn(
        '[version-check] Stale CDN detected: local=' + local +
        ' remote=' + remote + ' (URL already has ?v=' + local + ', not reloading again)',
      );
      return;
    }
    hardReload(remote);
  });

  // Focus check
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (staleHandled) return;
    void fetchRemoteVersion().then((remote) => {
      if (remote === null) return;
      if (remote === local) return;
      if (staleHandled) return;
      staleHandled = true;
      options.onStale(remote);
    });
  });
}

/**
 * Exposed for the modal's Reload Now button. Using the same hard-reload
 * path as the boot check keeps the query-string cache-busting behaviour
 * consistent.
 */
export function reloadToVersion(remoteVersion: string): void {
  hardReload(remoteVersion);
}
